import * as React from "react"
import { Head, useForm, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { AdminPageHeader } from "@/components/admin/AdminPageHeader"
import { Field, fieldAria } from "@/components/admin/Field"
import { ErrorSummary, FormActions, ImagePicker } from "@/components/admin/FormParts"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import type { AdminGalleryFormProps, PageProps } from "@/types/inertia"

type GalleryFormData = {
  title: string
  caption: string
  occasion: string
  image: File | null
}

export default function AdminGalleryForm() {
  const { props } = usePage<PageProps<AdminGalleryFormProps>>()
  const { item, occasions, caption_max_length: captionMax, submit_url: submitUrl, submit_method: submitMethod, back_url: backUrl } = props
  const editing = item.id !== null
  const title = editing ? `Edit ${item.title}` : "Add a gallery piece"
  const description = editing
    ? `Edit the gallery piece ${item.title}: its title, caption, occasion and image.`
    : "Add a bottle to the public gallery: its image, title, one-line caption and occasion."

  const form = useForm<GalleryFormData>({
    title: item.title,
    caption: item.caption,
    occasion: item.occasion,
    image: null,
  })
  const errors = form.errors as Partial<Record<keyof GalleryFormData, string>>
  const captionLength = form.data.caption.length

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    form.transform((data) => (submitMethod === "patch" ? { ...data, _method: "patch" } : data))
    form.post(submitUrl, { forceFormData: true, preserveState: "errors" })
  }

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <AdminShell>
        <AdminPageHeader
          title={title}
          description={
            editing
              ? "Changes show on the Gallery page and the occasion page straight away."
              : "New pieces join the end of the gallery; move them up from the list."
          }
          back={{ href: backUrl, label: "All gallery pieces" }}
        />

        <form onSubmit={submit} className="max-w-2xl" noValidate>
          <ErrorSummary errors={errors} />

          <div className="mt-8 space-y-6">
            <ImagePicker
              id="gallery-image"
              label={editing ? "Replace the image" : "Image"}
              hint="PNG, JPEG or WebP, up to 10 MB. The bottle on a plain background works best."
              currentUrl={item.image_url}
              currentAlt={`Current image of ${item.title}`}
              file={form.data.image}
              error={errors.image}
              onChange={(file) => form.setData("image", file)}
            />

            <Field id="gallery-title" label="Title" error={errors.title} hint="Usually the client or the event, such as “Royal Automobile Club”.">
              <Input
                {...fieldAria("gallery-title", { error: errors.title, hint: true })}
                className="form-control-large"
                value={form.data.title}
                onChange={(event) => form.setData("title", event.target.value)}
                required
              />
            </Field>

            <Field
              id="gallery-caption"
              label="Caption"
              error={errors.caption}
              hint={`One line, up to ${captionMax} characters (${captionLength} of ${captionMax}).`}
            >
              <Input
                {...fieldAria("gallery-caption", { error: errors.caption, hint: true })}
                className="form-control-large"
                value={form.data.caption}
                maxLength={captionMax}
                onChange={(event) => form.setData("caption", event.target.value)}
              />
            </Field>

            <Field id="gallery-occasion" label="Occasion" error={errors.occasion} hint="Filters the gallery and places the piece on that occasion's page.">
              <Select
                {...fieldAria("gallery-occasion", { error: errors.occasion, hint: true })}
                className="form-control-large"
                value={form.data.occasion}
                onChange={(event) => form.setData("occasion", event.target.value)}
                required
              >
                <option value="" disabled>
                  Choose an occasion…
                </option>
                {occasions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <FormActions
            processing={form.processing}
            submitLabel={editing ? "Save changes" : "Add to the gallery"}
            cancelHref={backUrl}
          />
        </form>
      </AdminShell>
    </>
  )
}
